import React, { createContext, useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

export const GlobalContext = createContext();

const hasRequiredRole = (roles, requiredRole) => {
  return roles.includes(requiredRole);
};

const parseStoredJson = (key, fallback) => {
  try {
    const stored = sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch {
    return fallback;
  }
};

const GlobalProvider = ({ children }) => {
  const [userId, setUserId] = useState(sessionStorage.getItem("userId"));
  const [displayName, setDisplayName] = useState(sessionStorage.getItem("displayName"));
  const [email, setEmail] = useState(sessionStorage.getItem("email"));
  const [roles, setRoles] = useState(() => parseStoredJson("roles", []));
  const [permissions, setPermissions] = useState(() => parseStoredJson("permissions", []));
  const [permissionsLoaded, setPermissionsLoaded] = useState(() => sessionStorage.getItem("permissions") !== null);
  const [userName, setUserName] = useState(sessionStorage.getItem("username"));
  const [profilePhotoFileId, setProfilePhotoFileId] = useState(() => {
    const stored = sessionStorage.getItem("profilePhotoFileId");
    return stored ? Number(stored) : null;
  });
  const [isAuthorized, setIsAuthorized] = useState(hasRequiredRole(roles, "DWSUser"));

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const getNotifications = async () => {
      try {
        const response = await axios.get("/api/v1/notifications");
        setNotifications(response.data);
        setUnreadCount(response.data.filter((n) => !n.isRead).length);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    getNotifications();
  }, [location]);

  const fetchUserData = async () => {
    try {
      const response = await axios.get("/api/v1/GetAppUserInformation");
      console.log("User info API response:", response.data);
      const { id, firstname, lastname, userName, email, roles, permissions, profilePhotoFileId: photoFileId } = response.data;
      const nextPermissions = Array.isArray(permissions) ? permissions : [];

      setUserId(id);
      setUserName(userName);
      setDisplayName(`${firstname} ${lastname}`);
      setEmail(email);
      setRoles(roles);
      setPermissions(nextPermissions);
      setPermissionsLoaded(true);
      setProfilePhotoFileId(photoFileId || null);

      sessionStorage.setItem("userId", id);
      sessionStorage.setItem("email", email);
      sessionStorage.setItem("roles", JSON.stringify(roles));
      sessionStorage.setItem("permissions", JSON.stringify(nextPermissions));
      sessionStorage.setItem("displayName", `${firstname} ${lastname}`);
      sessionStorage.setItem("userName", userName);
      if (photoFileId) {
        sessionStorage.setItem("profilePhotoFileId", String(photoFileId));
      } else {
        sessionStorage.removeItem("profilePhotoFileId");
      }

      setIsAuthorized(hasRequiredRole(roles, "DWSUser"));
    } catch (error) {
      console.error("Error fetching user info:", error);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith("/profile")) {
      fetchUserData();
    }
  }, [location.pathname]);

  const globalData = {
    userId,
    email,
    roles,
    permissions,
    permissionsLoaded,
    displayName,
    userName,
    profilePhotoFileId,
    isAuthorized: hasRequiredRole(roles, "DWSUser"),
    notifications,
    unreadCount,
  };

  return (
    <GlobalContext.Provider
      value={{
        globalData,
        isAuthorized,
        fetchUserData,
        notifications,
        setNotifications,
        unreadCount,
        setUnreadCount,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export default GlobalProvider;
